<template>
  <div id="email_activity_funnel">
    <ContainerInfoItem
      source="email_activity_funnel_graph"
      title="Email funnel"
      :description="range.label"
      height="fit-content"
    >
      <SkeletonLoader v-if="isLoading" :page="loaderPages.CHART" />

      <BarChart
        v-else-if="chartCategories && chartData"
        id="email_activity_funnel"
        :categories="chartCategories"
        :series-data="chartData"
      />

      <EmptyScreen
        v-else
        source="email_activity_funnel_graph"
        text="No data found!"
        :type="emptyScreenTypes.CHART_AND_TABLE"
      />
    </ContainerInfoItem>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watchEffect } from "vue";
import BarChart from "@/src/components/charts/BarChart.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import AnalyticService from "@/src/api/AnalyticService.js";
import { emptyScreenTypes } from "@/src/constants/searchConstants.js";
import store from "@/src/store/index.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const range = computed(() => store.getters["analytics/getRange"]);
const filters = computed(() => store.getters["analytics/getFilters"]);
const chartCategories = ref(null);
const chartData = ref(null);
const isLoading = ref(false);

const toFetchEmailCampaignFunnel = async () => {
  try {
    isLoading.value = true;

    const params = {
      email_id: filters.value?.mailbox?.id,
      start_time: range.value?.start,
      end_time: range.value?.end,
    };

    const response = await AnalyticService.V2.fetchEmailCampaignFunnel(params);
    if (response?.success && response?.data?.data) {
      const data = response.data.data;

      const metrics = [
        { label: "Emails Sent", key: "emails_sent" },
        { label: "Opened", key: "open_count" },
        { label: "Clicked", key: "click_count" },
        { label: "Replied", key: "reply_count" },
        { label: "Positive Replies", key: "positive_reply_count" },
      ];

      chartCategories.value = metrics.map((metric) => metric.label);
      chartData.value = [
        {
          name: "Email Funnel",
          data: metrics.map((metric) => data[metric.key] || 0),
        },
      ];

      const allZeros = chartData.value[0].data.every((value) => value === 0);
      if (allZeros) {
        chartCategories.value = null;
        chartData.value = null;
      }
    } else {
      chartCategories.value = null;
      chartData.value = null;
    }
  } catch (error) {
    console.error(
      "An error occurred while fetching email campaign funnel: ",
      error,
    );
    chartCategories.value = null;
    chartData.value = null;
  } finally {
    isLoading.value = false;
  }
};

watchEffect(() => {
  if (range.value) toFetchEmailCampaignFunnel();
});

useAnalyticsRefresh(toFetchEmailCampaignFunnel, [range]);
</script>
